/**
 * tryoutApi — thin fetch wrappers for the 3 Tryout v2 edge function phases.
 *
 * All three calls hit `tryout-generate` (phase 'empty' | 'stage') or
 * `tryout-refine`. The envelope is always `{ success: boolean, ... }`;
 * `TryoutApiError` carries the server's `code` (e.g. 'limit_reached',
 * 'refine_limit', 'invalid_token') so callers can branch on it.
 */
import type { BoxCoordinates } from '@/types';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

const headers = {
  'Content-Type': 'application/json',
  apikey: SUPABASE_ANON_KEY,
  Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
};

export class TryoutApiError extends Error {
  code?: string;
  constructor(message: string, code?: string) {
    super(message);
    this.name = 'TryoutApiError';
    this.code = code;
  }
}

interface ApiEnvelope {
  success: boolean;
  error?: string;
  code?: string;
}

async function post<T>(fn: string, body: unknown): Promise<T> {
  const response = await fetch(`${SUPABASE_URL}/functions/v1/${fn}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  const data = (await response.json()) as ApiEnvelope;
  if (!data.success) {
    throw new TryoutApiError(data.error ?? 'Fehler', data.code);
  }
  return data as unknown as T;
}

/** Normalized {x1,y1,x2,y2} (BoxOverlay's shape) → the server's {x,y,width,height} contract. */
function toServerBox(box: BoxCoordinates): { x: number; y: number; width: number; height: number } {
  return {
    x: Math.min(box.x1, box.x2),
    y: Math.min(box.y1, box.y2),
    width: Math.abs(box.x2 - box.x1),
    height: Math.abs(box.y2 - box.y1),
  };
}

// ============================================================================
// Phase 'empty' — tryout-generate
// ============================================================================

export interface GenerateEmptyArgs {
  token: string;
  runId: string;
  source: 'upload' | 'preset';
  imageBase64: string;
  mimeType: string;
  roomType: string;
  keepKitchen: boolean;
  aspectRatio: '1:1' | '4:3';
}

export interface GenerateEmptyResult {
  success: true;
  runId: string;
  remaining: number;
  resultId: string;
  emptyVariants: { base64: string; variant: 'A' | 'B' }[];
}

export function generateEmpty(args: GenerateEmptyArgs): Promise<GenerateEmptyResult> {
  return post<GenerateEmptyResult>('tryout-generate', { ...args, phase: 'empty', variantCount: 2 });
}

// ============================================================================
// Phase 'stage' — tryout-generate
// ============================================================================

export interface GenerateStageArgs {
  token: string;
  runId: string;
  source: 'upload' | 'preset';
  imageBase64: string;
  mimeType: string;
  roomType: string;
  aspectRatio: '1:1' | '4:3';
  stagingStyle: string;
}

export interface GenerateStageResult {
  success: true;
  runId: string;
  stagedBase64: string;
  resultId: string;
  remaining: number;
}

export function generateStage(args: GenerateStageArgs): Promise<GenerateStageResult> {
  return post<GenerateStageResult>('tryout-generate', { ...args, phase: 'stage' });
}

// ============================================================================
// tryout-refine
// ============================================================================

export interface RefineEmptyArgs {
  token: string;
  runId: string;
  emptyBase64: string;
  mimeType: string;
  roomType: string;
  userPrompt: string;
  boxCoordinates?: BoxCoordinates;
}

export interface RefineEmptyResult {
  success: true;
  runId: string;
  refinedBase64?: string;
  regenerate_suggested?: boolean;
  regenerate_reason?: string;
  refineCount?: number;
  remaining: number;
  scope?: string;
  intent?: string;
}

export function refineEmpty(args: RefineEmptyArgs): Promise<RefineEmptyResult> {
  const { boxCoordinates, ...rest } = args;
  return post<RefineEmptyResult>('tryout-refine', {
    ...rest,
    boxCoordinates: boxCoordinates ? toServerBox(boxCoordinates) : undefined,
  });
}
